import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, ShoppingCart, ChevronLeft, Truck, ShieldCheck, RefreshCcw } from "lucide-react";
import { api, getProductImage, PRODUCT_PLACEHOLDER, formatPrice } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        return api.get("/products", { params: { category: r.data.category_slug, limit: 5 } });
      })
      .then((r) => setRelated(r.data.items.filter((i) => i.id !== id).slice(0, 4)))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="h-96 animate-pulse rounded-2xl bg-slate-100" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 lg:px-8" data-testid="product-not-found">
        <h1 className="font-display text-3xl font-bold text-brand-navy">Product not found</h1>
        <Link to="/shop" className="mt-4 inline-block text-brand-teal font-bold">← Back to shop</Link>
      </div>
    );
  }

  const img = getProductImage(product);

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`Added ${qty} × ${product.name}`, {
      description: `Subtotal: ${formatPrice(product.price * qty)}`,
    });
  };

  const handleBuyNow = () => {
    addItem(product, qty);
    navigate("/checkout");
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="product-detail-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-6 inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-brand-navy"
        data-testid="back-button"
      >
        <ChevronLeft size={16} /> Back
      </button>

      <div className="grid gap-10 lg:grid-cols-2">
        <div className="relative aspect-square overflow-hidden rounded-3xl border border-slate-200 bg-white">
          <img
            src={img}
            alt={product.name}
            onError={(e) => {
              if (!e.currentTarget.src.endsWith(PRODUCT_PLACEHOLDER)) e.currentTarget.src = PRODUCT_PLACEHOLDER;
            }}
            className="h-full w-full object-cover"
            data-testid="product-image"
          />
          {product.in_stock && product.stock <= 5 && (
            <span className="absolute right-4 top-4 chip bg-brand-teal text-white">Only {product.stock} left</span>
          )}
        </div>

        <div>
          <Link
            to={`/shop/${product.category_slug}`}
            className="chip bg-brand-teal/15 text-brand-teal hover:bg-brand-teal/25"
            data-testid="product-category-link"
          >
            {product.category}
          </Link>
          <h1 className="mt-3 font-display text-3xl font-bold text-brand-navy sm:text-4xl" data-testid="product-name">
            {product.name}
          </h1>
          {product.sku && (
            <p className="mt-2 font-mono text-xs text-slate-500">SKU · {product.sku}</p>
          )}

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-display text-4xl font-extrabold text-brand-navy" data-testid="product-price">
              {formatPrice(product.price)}
            </span>
            {product.in_stock ? (
              <span className="chip bg-emerald-50 text-emerald-700">In stock</span>
            ) : (
              <span className="chip bg-red-50 text-red-700">Out of stock</span>
            )}
          </div>

          {product.description && (
            <p className="mt-6 text-slate-600 leading-relaxed" data-testid="product-description">
              {product.description}
            </p>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="inline-flex items-center overflow-hidden rounded-full border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => setQty((n) => Math.max(1, n - 1))}
                className="p-3 text-brand-navy hover:bg-slate-100"
                aria-label="Decrease quantity"
                data-testid="qty-decrease"
              >
                <Minus size={14} />
              </button>
              <span className="min-w-[2ch] text-center font-bold text-brand-navy" data-testid="qty-value">
                {qty}
              </span>
              <button
                type="button"
                onClick={() => setQty((n) => Math.min(product.stock || 99, n + 1))}
                className="p-3 text-brand-navy hover:bg-slate-100"
                aria-label="Increase quantity"
                data-testid="qty-increase"
              >
                <Plus size={14} />
              </button>
            </div>

            <button
              type="button"
              onClick={handleAdd}
              disabled={!product.in_stock}
              className="btn-accent inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wide disabled:cursor-not-allowed disabled:bg-slate-300"
              data-testid="add-to-cart-button"
            >
              <ShoppingCart size={16} /> Add to Cart
            </button>

            <button
              type="button"
              onClick={handleBuyNow}
              disabled={!product.in_stock}
              className="btn-primary inline-flex items-center rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wide disabled:cursor-not-allowed"
              data-testid="buy-now-button"
            >
              Buy now
            </button>
          </div>

          <ul className="mt-8 space-y-3 border-t border-slate-200 pt-6 text-sm text-slate-600">
            <li className="flex items-center gap-3">
              <Truck size={16} className="text-brand-teal" /> Free shipping on orders $50+
            </li>
            <li className="flex items-center gap-3">
              <ShieldCheck size={16} className="text-brand-teal" /> Secure checkout · powered by Square
            </li>
            <li className="flex items-center gap-3">
              <RefreshCcw size={16} className="text-brand-teal" /> 14-day return on sealed product
            </li>
          </ul>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-16" data-testid="product-related">
          <h2 className="mb-6 font-display text-2xl font-bold text-brand-navy">You might also like</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {related.map((r) => (
              <ProductCard key={r.id} product={r} testIdPrefix="related" />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
